import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiAgeProgression = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-age-progression',
			name: 'AI Age Progression — Face Aging Tool by Lift',
			description:
				'Upload a portrait and preview realistic AI age progression across 6 age stages. Compare your original photo with AI-generated results instantly. Try Lift now.',
			url: 'https://liftapp.ai/ai-age-progression',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-age-progression#product' },
				{ '@id': 'https://liftapp.ai/ai-age-progression#howto' },
				{ '@id': 'https://liftapp.ai/ai-age-progression#benefits' },
				{ '@id': 'https://liftapp.ai/ai-age-progression#faq' },
				{ '@id': 'https://liftapp.ai/ai-age-progression#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-age-progression#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-age-progression#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-age-progression#product',
			name: 'AI Age Progression — Face Aging Tool by Lift',
			description:
				'Upload a portrait and preview realistic AI age progression across 6 age stages. Compare your original photo with AI-generated results instantly. Try Lift now.',
			url: 'https://liftapp.ai/ai-age-progression',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-age-progression#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: 'Choose a clear portrait from your device to start the age progression preview',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose an age',
					text: 'Pick one of 6 age options to see how your face changes over time',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Compare the result',
					text: 'View your original image next to the AI-generated age progression result',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Regenerate anytime',
					text: 'Try another age option and create a new version with one tap',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-age-progression#benefits',
			name: 'Who can benefit from AI Age Progression?',
			description:
				"Lift's AI Age Progression is useful for social media users, curious individuals, friends, content creators, and mobile users who want to preview how a face changes across different life stages.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social media users can generate compelling before-and-after age progression photos to share across platforms',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Curious users can preview how their face might look at different life stages with a simple upload',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Friends and entertainment seekers can run age progressions on group photos and compare results together',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators can produce eye-catching AI aging visuals for reels, thumbnails, or concept posts',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Mobile users can upload a photo, select an age, and get a result in seconds from any device',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Age Reversal – Adult to Child Face Transformation with Lift',
				description:
					'Before and after comparison showing AI age reversal in Lift, transforming an adult woman into a young child version of the same person.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-age-progression#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Age Progression?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Age Progression is a visual feature that transforms a single uploaded portrait into older-looking versions using AI. It lets you explore how a face might appear across different life stages, with results generated in seconds.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Age Progression work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a clear portrait, then select one of 6 age options from the on-screen control. Lift generates an aged version of the face and displays it alongside your original photo for an instant side-by-side comparison.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I choose different age options?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Age Progression includes 6 age options, so you can preview different older versions of the same portrait and compare the results easily.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-age-progression#why-lift',
			name: "Why Lift's AI Age Progression stands out",
			description:
				"Lift's AI Age Progression combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'AI Face De-Aging Result – Senior to Young Adult Transformation in Lift',
				description:
					'Side-by-side portrait showing an elderly woman transformed into a younger version of herself using Lift AI age progression technology.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-age-progression#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-age-progression#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Age Progression',
					item: 'https://liftapp.ai/ai-age-progression',
				},
			],
		},
	],
};
